import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type React from "react";
import { ApiError } from "@/api/apiClient";
import { ThemeProvider } from "@/hooks/useTheme";
import "@/styles/globals.css";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5,
			retry: (failureCount, error) => {
				if (error instanceof ApiError) {
					if (error.status === 429) return failureCount < 5;
					if (error.status >= 400 && error.status < 500) return false;
				}

				return failureCount < 3;
			},
			retryDelay: (attemptIndex, error) => {
				if (error instanceof ApiError && error.status === 429 && typeof error.retryAfterMs === "number") {
					return error.retryAfterMs;
				}

				return Math.min(1000 * 2 ** attemptIndex, 30_000);
			},
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		},
		mutations: {
			retry: (failureCount, error) => {
				if (error instanceof ApiError) {
					if (error.status === 429) return failureCount < 3;
					if (error.status >= 400 && error.status < 500) return false;
				}
				return failureCount < 1;
			},
			retryDelay: (attemptIndex, error) => {
				if (error instanceof ApiError && error.status === 429 && typeof error.retryAfterMs === "number") {
					return error.retryAfterMs;
				}
				return Math.min(1000 * 2 ** attemptIndex, 30_000);
			},
		},
	},
});

interface AppProvidersProps {
	children: React.ReactNode;
}

const AppProviders = ({ children }: AppProvidersProps) => {
	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider>{children}</ThemeProvider>
		</QueryClientProvider>
	);
};

export default AppProviders;
